// Crafting service for frontend logic
import api from '../utils/api.js';

class CraftingService {
  constructor() {
    this.recipes = [];
    this.loading = false;
    this.error = null;
  }

  // Cache de recetas
  async loadRecipes() {
    if (this.recipes.length > 0) {
      return this.recipes;
    }

    try {
      this.loading = true;
      this.error = null;
      
      const response = await api.getRecipes();
      
      if (response.success) {
        this.recipes = response.recipes || [];
        return this.recipes;
      } else {
        throw new Error(response.error || 'Failed to load recipes');
      }
    } catch (error) {
      this.error = error.message || 'Failed to load recipes';
      console.error('Error loading recipes:', error);
      throw error;
    } finally {
      this.loading = false;
    }
  }

  // Helper para verificar si se puede craftear una receta
  canCraft(recipe, playerInventory, playerLevel) {
    const issues = [];

    // Verificar nivel del jugador
    if (playerLevel < recipe.levelReq) {
      issues.push(`Requiere nivel ${recipe.levelReq} (tienes nivel ${playerLevel})`);
    }

    // Verificar ingredientes en inventario
    const inventoryMap = new Map(
      playerInventory.map(item => [item.itemId, item.qty])
    );

    for (const ingredient of recipe.ingredients) {
      const playerQty = inventoryMap.get(ingredient.itemId) || 0;
      if (playerQty < ingredient.qty) {
        const missing = ingredient.qty - playerQty;
        issues.push(`Necesitas ${missing} más de ${ingredient.item.name}`);
      }
    }

    return {
      canCraft: issues.length === 0,
      issues: issues
    };
  }

  // Helper para generar tooltip de receta
  getRecipeTooltip(recipe, playerInventory, playerLevel) {
    const check = this.canCraft(recipe, playerInventory, playerLevel);
    
    let tooltip = `<strong>${recipe.name}</strong><br>`;
    tooltip += `Nivel requerido: ${recipe.levelReq}<br>`;
    tooltip += `Resultado: ${recipe.outputQty}x ${recipe.outputItem.name}<br><br>`;
    tooltip += '<strong>Ingredientes:</strong><br>';
    
    const inventoryMap = new Map(
      playerInventory.map(item => [item.itemId, item.qty])
    );

    for (const ingredient of recipe.ingredients) {
      const playerQty = inventoryMap.get(ingredient.itemId) || 0;
      const hasEnough = playerQty >= ingredient.qty;
      const color = hasEnough ? '#4ade80' : '#f87171';
      
      tooltip += `<span style="color: ${color}">`;
      tooltip += `${ingredient.qty}x ${ingredient.item.name} `;
      tooltip += `(tienes ${playerQty})`;
      tooltip += '</span><br>';
    }

    if (!check.canCraft) {
      tooltip += '<br><strong style="color: #f87171">No se puede craftear:</strong><br>';
      tooltip += check.issues.map(issue => `• ${issue}`).join('<br>');
    }

    return tooltip;
  }

  // Craftear item
  async craft(recipeId, times = 1) {
    try {
      const response = await api.postCraft({ recipeId, times });
      
      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Failed to craft item');
      }
    } catch (error) {
      console.error('Error crafting item:', error);
      throw error;
    }
  }

  // Obtener receta por ID
  getRecipeById(recipeId) {
    return this.recipes.find(recipe => recipe.id === recipeId);
  }

  // Obtener receta por slug
  getRecipeBySlug(slug) {
    return this.recipes.find(recipe => recipe.slug === slug);
  }

  // Filtrar recetas por nivel disponible
  getAvailableRecipes(playerLevel) {
    return this.recipes.filter(recipe => recipe.levelReq <= playerLevel);
  }

  // Limpiar cache
  clearCache() {
    this.recipes = [];
    this.error = null;
  }
}

// Export singleton
export const craftingService = new CraftingService();
export default craftingService;